'use client';

import { faTimes } from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Button, Drawer } from '@pickleballinc/react-ui';
import { useEffect, useState } from 'react';

import { AdditionalTourneyEventDetails } from '@/app/tournaments/api/eventDetails/route';
import { getPlayersOrTeamsText } from '@/utils/helpers/helpers';

interface EventDetailsDrawerProps {
	showEventDetails: boolean;
	setShowEventDetails: React.Dispatch<React.SetStateAction<boolean>>;
	tourneyId: string;
	activityId: string;
}

export const EventDetailsDrawer = ({ showEventDetails, setShowEventDetails, activityId, tourneyId }: EventDetailsDrawerProps) => {
	const [eventDetails, setEventDetails] = useState<AdditionalTourneyEventDetails>();
	const getEventDetails = async () => {
		const response = await fetch(`/tournaments/api/eventDetails?activityId=${activityId}&tourneyId=${tourneyId}`);

		const eventDetails = (await response.json()) as { data: AdditionalTourneyEventDetails };

		setEventDetails(eventDetails.data);
	};

	useEffect(() => {
		if (showEventDetails) getEventDetails();
	}, [showEventDetails]);

	const isBracketFormat4Valid =
		eventDetails?.BracketFormatID === 4 &&
		eventDetails?.Score_RR_DoSingleElimMedalBracket !== 0 &&
		!(eventDetails?.Score_RR_DoSingleElimMedalBracket === 1 && eventDetails?.Score_RR_TeamCountAdvanceToSingleElimMedalBracket === 3) &&
		!(
			eventDetails?.Score_RR_TeamCountAdvanceToSingleElimMedalBracket === 4 &&
			eventDetails?.MedalRound_SingleElim_CustomType === '1st2ndToGoldSilver3rd4thToBronze'
		);

	const shouldShowWinnerBracketScore = isBracketFormat4Valid || eventDetails?.BracketFormatID === 1 || eventDetails?.BracketFormatID === 5;

	const getBronzeScoreText = () => {
		if (
			eventDetails?.Score_RR_DoSingleElimMedalBracket === 1 &&
			eventDetails?.Score_RR_TeamCountAdvanceToSingleElimMedalBracket === 3 &&
			eventDetails?.MedalRound_SingleElim_CustomType === '3rdAutoBronze'
		) {
			return '3rd place from RR';
		}
		return eventDetails?.ScoreFormat_B_Title;
	};

	return (
		<Drawer open={showEventDetails} wrapperClassname="sm:max-w-[60vw] overflow-auto" onBackdropClick={() => setShowEventDetails(false)}>
			{eventDetails && (
				<div className="flex flex-col  bg-white">
					<div className="flex flex-row justify-between bg-gray-800 p-4 text-lg font-semibold text-white">
						<div>{eventDetails?.Title}</div>
						<Button onClick={() => setShowEventDetails(false)} variant="link-gray" className="!text-white">
							<FontAwesomeIcon icon={faTimes}></FontAwesomeIcon>
						</Button>
					</div>
					<div className="flex flex-col gap-4 p-4 text-black">
						<div className="gap-4 rounded-lg border border-gray-200 p-4">
							<div className="mb-4 font-bold">General Info</div>
							<div className="flex flex-col gap-2 text-sm">
								<div className="flex flex-row items-center">
									<div className="w-[130px] min-w-[130px] bg-blue-25 p-2">Date</div>
									<div className="p-2">{eventDetails?.date}</div>
								</div>
								<div className="flex flex-row items-center">
									<div className="w-[130px] min-w-[130px] bg-blue-25 p-2">Results to DUPR?</div>
									<div className="p-2">{eventDetails?.CountGamesTowardPickleballBracketsRatings !== 0 ? 'Yes' : 'No'}</div>
								</div>
								<div className="flex flex-row items-center">
									<div className="w-[130px] min-w-[130px] bg-blue-25 p-2">
										Max # of {getPlayersOrTeamsText(eventDetails?.FormatID)}
									</div>
									<div className="p-2">{eventDetails?.MaxAttendies === -1 ? 'Unlimited' : eventDetails?.MaxAttendies}</div>
								</div>
								<div className="flex flex-row items-center">
									<div className="w-[130px] min-w-[130px] bg-blue-25 p-2">Players Registered</div>
									<div className="p-2">{eventDetails?.TotalRegistrations}</div>
								</div>
								<div className="flex flex-row items-center">
									<div className="w-[130px] min-w-[130px] bg-blue-25 p-2">Waitlist</div>
									<div className="p-2">{eventDetails?.TotalRegistrations_OnHold}</div>
								</div>
								<div className="flex flex-row items-center">
									<div className="w-[130px] min-w-[130px] bg-blue-25 p-2">Bracket Format</div>
									<div className="p-2">{eventDetails?.BracketFormatTitle}</div>
								</div>
								<div className="flex flex-row items-center">
									<div className="w-[130px] min-w-[130px] bg-blue-25 p-2">Player Group</div>
									<div className="p-2">{eventDetails?.PlayerGroupTitle}</div>
								</div>
								<div className="flex flex-row items-center">
									<div className="w-[130px] min-w-[130px] bg-blue-25 p-2">Format</div>
									<div className="p-2">{eventDetails?.FormatTitle}</div>
								</div>
								<div className="flex flex-row items-center">
									<div className="w-[130px] min-w-[130px] bg-blue-25 p-2">Skill</div>
									<div className="p-2">{eventDetails?.RankRangeForUI}</div>
								</div>
								<div className="flex flex-row items-center">
									<div className="w-[130px] min-w-[130px] bg-blue-25 p-2">Age</div>
									<div className="p-2">{eventDetails?.AgeRangeForUI}</div>
								</div>
							</div>
						</div>

						{/* Round Robin and Double Round-Robin */}
						{[4, 6].includes(eventDetails?.BracketFormatID || 0) && (
							<div className="gap-4 rounded-lg border border-gray-200 p-4">
								<div className="mb-4 font-bold">Round Robin Information</div>
								<div className="flex flex-col gap-2 text-sm">
									<div className="flex flex-row items-center">
										<div className="w-[130px] min-w-[130px] bg-blue-25 p-2">1st Place</div>
										<div className="p-2">Match Wins</div>
									</div>
									<div className="flex flex-row items-center">
										<div className="w-[130px] min-w-[130px] bg-blue-25 p-2">2nd Place</div>
										<div className="p-2">Point differential within the head to head</div>
									</div>
									<div className="flex flex-row items-center">
										<div className="w-[130px] min-w-[130px] bg-blue-25 p-2">3rd Place</div>
										<div className="p-2">Point differential within the entire pool</div>
									</div>
									<div className="flex flex-row items-center">
										<div className="w-[130px] min-w-[130px] bg-blue-25 p-2">4th Place</div>
										<div className="p-2">Point differential against the next highest ranked team</div>
									</div>

									<div className="mt-8 flex flex-row">
										<div className="w-[130px] min-w-[130px] bg-blue-25 p-2">Medal Round</div>
										<div className="p-2">
											{eventDetails?.Score_RR_DoSingleElimMedalBracket === 1
												? 'Yes - Single Elimination'
												: eventDetails?.Score_RR_DoSingleElimMedalBracket == 2
													? 'Yes - Double Elimination'
													: 'No'}
										</div>
									</div>

									{eventDetails?.Score_RR_DoSingleElimMedalBracket !== 0 && (
										<div className="flex flex-row items-center">
											<div className="w-[130px] min-w-[130px] bg-blue-25 p-2">Number of Teams to Advance</div>
											<div className="p-2">
												{eventDetails?.Score_RR_TeamCountAdvanceToSingleElimMedalBracket} from Round Robin
											</div>
										</div>
									)}
								</div>
							</div>
						)}

						<div className="gap-4 rounded-lg border border-gray-200 p-4">
							<div className="mb-4 font-bold">Score Format</div>
							<div className="flex flex-col gap-2 text-sm">
								{[4, 6].includes(eventDetails?.BracketFormatID || 0) && (
									<div className="flex flex-row items-center">
										<div className="w-[130px] min-w-[130px] bg-blue-25 p-2">Round Robin</div>
										<div className="p-2">{eventDetails?.ScoreFormat_RR_Title}</div>
									</div>
								)}

								<div className="flex flex-row">
									<div className="flex w-[130px] min-w-[130px] bg-blue-25 p-2">
										<div className="my-auto">Medal Round</div>
									</div>
									<div className="flex w-full flex-col">
										{shouldShowWinnerBracketScore && (
											<div className="flex w-full flex-row flex-wrap items-center gap-1 p-2">
												<div className="font-medium">Winner: </div>
												<div>{eventDetails?.ScoreFormat_W_Title}</div>
											</div>
										)}

										<div className="flex w-full flex-row flex-wrap items-center gap-1 p-2">
											<div className="font-medium">Gold/Silver:</div>
											<div>{eventDetails?.ScoreFormat_GS_Title}</div>
										</div>

										{((eventDetails?.BracketFormatID === 4 && eventDetails?.Score_RR_DoSingleElimMedalBracket === 2) ||
											eventDetails?.BracketFormatID === 5) &&
											eventDetails?.DoubleElimLoserCannotGetBackToWinner === 0 && (
												<div className="flex w-full flex-row flex-wrap items-center gap-1 p-2">
													<div className="font-medium">Gold/Silver (if winners first loss):</div>
													<div>{eventDetails?.ScoreFormat_GS_IfFirstLoss_Title}</div>
												</div>
											)}

										<div className="flex w-full flex-row flex-wrap items-center gap-1 p-2">
											<div className="font-medium">Bronze:</div>
											<div>{getBronzeScoreText()}</div>
										</div>

										{((eventDetails?.BracketFormatID === 4 && eventDetails?.Score_RR_DoSingleElimMedalBracket === 2) ||
											eventDetails?.BracketFormatID === 5) && (
											<div className="flex w-full flex-row flex-wrap items-center gap-1 p-2">
												<div className="font-medium">Backdraw:</div>
												<div>{eventDetails?.ScoreFormat_L1_Title}</div>
											</div>
										)}
									</div>
								</div>
							</div>
						</div>
					</div>
				</div>
			)}
		</Drawer>
	);
};
